// FILE: src/components/MessagesIndexModal.js
import React, { useEffect, useMemo, useState, useRef } from "react";
import PropTypes from "prop-types";
import Modal from "./Modal";
import "./css/MessagesInboxModal.css";

import {
    collection,
    query,
    where,
    orderBy,
    onSnapshot,
    addDoc,
    serverTimestamp,
    doc,
    getDoc,
    updateDoc,
    writeBatch,
    getDocs,
    setDoc,
    arrayUnion
} from "firebase/firestore";

export default function MessagesIndexModal({
    open,
    onClose,
    db,
    auth = null,
    user,
    useFirebase = false,
    setToast,
    initialPartner = null
}) {
    const currentUid = user?.uid || null;

    const [conversations, setConversations] = useState([]);
    const [selectedPartnerUid, setSelectedPartnerUid] = useState(null);
    const [selectedPartnerProfile, setSelectedPartnerProfile] = useState(null);
    const [threadMessages, setThreadMessages] = useState([]);
    const [threadLoading, setThreadLoading] = useState(false);
    const [conversationsLoading, setConversationsLoading] = useState(false);
    const [composerText, setComposerText] = useState("");
    const [sending, setSending] = useState(false);
    const [activeTab, setActiveTab] = useState("primary");
    const [search, setSearch] = useState("");
    const [followingSet, setFollowingSet] = useState(new Set());
    const [profilesMap, setProfilesMap] = useState({}); // { [uid]: profileObj }
    const composerRef = useRef(null);
    const searchRef = useRef(null);
    const modalContentRef = useRef(null);

    const conversationIdFor = (a, b) => {
        if (!a || !b) return null;
        return [a, b].sort().join("_");
    };

    // Classification helpers
    const primaryConvs = useMemo(() => conversations.filter((c) => c.classification === "primary"), [conversations]);
    const businessConvs = useMemo(() => conversations.filter((c) => c.classification === "business"), [conversations]);
    const generalConvs = useMemo(() => conversations.filter((c) => c.classification === "general" || !c.classification), [conversations]);

    // ensure the modal opens the thread for an initial partner when requested
    useEffect(() => {
        if (!initialPartner) return;
        // only set when modal is open to avoid surprising behavior
        if (!open) return;

        // only update if different
        setSelectedPartnerUid((prev) => {
            if (prev === initialPartner) return prev;
            // reset profile & composer so the UI focuses the thread
            setSelectedPartnerProfile(null);
            setComposerText("");
            return initialPartner;
        });
    }, [initialPartner, open]);

    // close modal when clicking outside the modal content
    useEffect(() => {
        if (!open) return;

        function handleClickOutside(e) {
            // if click is outside the modal content, close the modal
            if (modalContentRef.current && !modalContentRef.current.contains(e.target)) {
                // mirror the existing onClose cleanup you use when closing the Modal
                setSelectedPartnerUid(null);
                setSelectedPartnerProfile(null);
                setComposerText("");
                onClose?.();
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open, onClose]);


    /* -------------------------
       Load following set (listens to follows collection)
       ------------------------- */
    useEffect(() => {
        if (!db || !currentUid || !useFirebase) {
            setFollowingSet(new Set());
            return;
        }

        // prefer provided array on user object
        if (Array.isArray(user?.following) && user.following.length >= 0) {
            setFollowingSet(new Set(user.following));
            return;
        }

        let mounted = true;
        try {
            const followsRef = collection(db, "follows");
            const q = query(followsRef, where("followerId", "==", currentUid));

            const unsub = onSnapshot(
                q,
                (snap) => {
                    if (!mounted) return;
                    const s = new Set();
                    snap.docs.forEach((d) => {
                        const data = d.data();
                        if (data?.followeeId) s.add(String(data.followeeId).trim());
                    });
                    setFollowingSet(s);
                },
                (err) => {
                    console.error("Failed to listen follows:", err);
                    setToast?.({ msg: "Failed to load follow list", type: "warning" });
                    if (mounted) setFollowingSet(new Set());
                }
            );

            return () => {
                mounted = false;
                unsub();
            };
        } catch (err) {
            console.error("fetchFollowing error:", err);
            setToast?.({ msg: "Failed to load follow list", type: "warning" });
            setFollowingSet(new Set());
        }
    }, [db, currentUid, useFirebase, user, setToast]);

    /* -------------------------
       Subscribe conversations and compute classification (primary/general)
       ------------------------- */
    useEffect(() => {
        if (!db || !currentUid || !useFirebase || !open) {
            setConversations([]);
            return;
        }

        setConversationsLoading(true);
        const convsRef = collection(db, "messages");
        const q = query(convsRef, where("participants", "array-contains", currentUid), orderBy("lastUpdated", "desc"));

        const unsub = onSnapshot(
            q,
            (snap) => {
                const convs = snap.docs.map((d) => {
                    const data = d.data() || {};
                    const participants = data.participants || [];
                    const partnerUid = participants.find((p) => p !== currentUid) || null;

                    // don't overwrite 'business' classification
                    let classification = data.classification;
                    if (!classification || classification === "general") {
                        if (partnerUid && followingSet.has(partnerUid)) classification = "primary";
                        else classification = data.classification || "general";
                    }

                    return {
                        id: d.id,
                        ...data,
                        classification,
                        partnerUid
                    };
                });

                setConversations(convs);
                setConversationsLoading(false);
            },
            (err) => {
                console.error("conversation listener error:", err);
                setToast?.({ msg: "Failed to load conversations", type: "warning" });
                setConversationsLoading(false);
            }
        );

        return () => unsub();
    }, [db, currentUid, useFirebase, open, followingSet, setToast]);

    /* -------------------------
       Thread listener when selected partner changes
       ------------------------- */
    useEffect(() => {
        if (!db || !currentUid || !useFirebase || !selectedPartnerUid) {
            setThreadMessages([]);
            setThreadLoading(false);
            return;
        }

        const convId = conversationIdFor(currentUid, selectedPartnerUid);
        if (!convId) {
            setThreadMessages([]);
            return;
        }

        setThreadLoading(true);
        const itemsRef = collection(db, "messages", convId, "items");
        const q = query(itemsRef, orderBy("createdAt", "asc"));

        const unsub = onSnapshot(
            q,
            (snap) => {
                const msgs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
                setThreadMessages(msgs);
                setThreadLoading(false);

                // mark as read (best-effort)
                markConversationReadSubcollection(db, currentUid, selectedPartnerUid, setToast).catch((e) => {
                    console.error("mark read failed:", e);
                });
            },
            (err) => {
                console.error("thread listener error:", err);
                setToast?.({ msg: "Failed to load thread", type: "warning" });
                setThreadLoading(false);
            }
        );

        return () => unsub();
    }, [db, currentUid, useFirebase, selectedPartnerUid, setToast]);

    /* -------------------------
       Load partner profile when selectedPartnerUid changes (if not present on conv doc)
       ------------------------- */
    useEffect(() => {
        if (!db || !selectedPartnerUid) {
            setSelectedPartnerProfile(null);
            return;
        }

        const already = conversations.find((c) => {
            const partnerUid = (c.participants || []).find((p) => p !== currentUid);
            return partnerUid === selectedPartnerUid && c.partnerProfile;
        });

        if (already?.partnerProfile) {
            setSelectedPartnerProfile(already.partnerProfile);
            return;
        }

        let mounted = true;
        async function fetchProfile() {
            try {
                console.log("fetchProfile: currentUid=", currentUid, "selectedPartnerUid=", selectedPartnerUid, "db=", !!db);
                const userRef = doc(db, "users", selectedPartnerUid);
                const userSnap = await getDoc(userRef);
                console.log("getDoc(users) result:", userSnap.exists(), userSnap?.data ? Object.keys(userSnap.data()) : null);
                if (userSnap.exists()) {
                    setSelectedPartnerProfile(userSnap.data());
                    return;
                }

                const profRef = doc(db, "profiles", selectedPartnerUid);
                const profSnap = await getDoc(profRef);
                console.log("getDoc(profiles) result:", profSnap.exists(), profSnap?.data ? Object.keys(profSnap.data()) : null);
                if (profSnap.exists()) {
                    setSelectedPartnerProfile(profSnap.data());
                    return;
                }

                setSelectedPartnerProfile(null);
            } catch (err) {
                console.error("fetchProfile permission/error:", err);
                // inspect code to give better UX and understanding
                if (err?.code === "permission-denied") {
                    setToast?.({ msg: "Permission denied when loading partner profile", type: "warning" });
                } else {
                    setToast?.({ msg: "Failed to load partner profile", type: "warning" });
                }
            }
        }


        fetchProfile();
        return () => { mounted = false; };
    }, [db, selectedPartnerUid, conversations, currentUid, setToast]);

    // preload missing partner profiles after conversations load
    useEffect(() => {
        if (!db || !conversations || conversations.length === 0) return;

        // collect partner UIDs that are missing profile info both on conv and in profilesMap
        const missing = new Set();
        conversations.forEach((c) => {
            const partnerUid = c.partnerUid || (c.participants || []).find((p) => p !== currentUid);
            if (!partnerUid) return;
            const hasConvProfile = c.partnerProfile && Object.keys(c.partnerProfile).length > 0;
            if (!hasConvProfile && !profilesMap[partnerUid]) missing.add(partnerUid);
        });

        if (missing.size === 0) return;

        let mounted = true;
        (async () => {
            try {
                const ids = Array.from(missing);
                const results = await Promise.all(
                    ids.map(async (uid) => {
                        try {
                            // try users/{uid}
                            const userRef = doc(db, "users", uid);
                            const userSnap = await getDoc(userRef);
                            if (userSnap.exists()) return [uid, userSnap.data()];

                            // fallback to profiles/{uid}
                            const profRef = doc(db, "profiles", uid);
                            const profSnap = await getDoc(profRef);
                            if (profSnap.exists()) return [uid, profSnap.data()];

                            return [uid, null];
                        } catch (e) {
                            console.error("fetch profile error for", uid, e);
                            return [uid, null];
                        }
                    })
                );

                if (!mounted) return;

                setProfilesMap((prev) => {
                    const next = { ...prev };
                    results.forEach(([uid, profile]) => {
                        if (profile) next[uid] = profile;
                    });
                    return next;
                });
            } catch (err) {
                console.error("preload profiles failed:", err);
                setToast?.({ msg: "Failed to load some profiles", type: "warning" });
            }
        })();

        return () => {
            mounted = false;
        };
    }, [conversations, db, currentUid, profilesMap, setToast]);


    /* -------------------------
       Send message
       ------------------------- */
    async function handleSendToPartner(e) {
        e?.preventDefault?.();
        if (!composerText.trim()) return;
        if (!useFirebase || !db || !currentUid || !selectedPartnerUid) {
            setToast?.({ msg: "Cannot send — sign in or check configuration", type: "warning" });
            return;
        }
        setSending(true);

        const convId = conversationIdFor(currentUid, selectedPartnerUid);
        const convRef = doc(db, "messages", convId);
        const itemsColRef = collection(db, "messages", convId, "items");

        const text = composerText.trim().slice(0, 1024);
        const messagePayload = {
            from: currentUid,
            to: selectedPartnerUid,
            text,
            createdAt: serverTimestamp(),
            read: false
        };

        try {
            const desiredClassification = followingSet.has(selectedPartnerUid) ? "primary" : "general";

            await setDoc(
                convRef,
                {
                    participants: arrayUnion(currentUid, selectedPartnerUid),
                    participantMap: { [currentUid]: true, [selectedPartnerUid]: true },
                    lastMessage: { text, from: currentUid, to: selectedPartnerUid, createdAt: serverTimestamp() },
                    lastUpdated: serverTimestamp(),
                    classification: desiredClassification
                },
                { merge: true }
            );

            await addDoc(itemsColRef, messagePayload);

            setComposerText("");
            const localMsg = { id: `local-${Date.now()}`, ...messagePayload, createdAt: new Date().toISOString() };
            setThreadMessages((prev) => [...prev, localMsg]);
            setToast?.({ msg: "Message sent", type: "success" });
            composerRef.current?.focus();
        } catch (err) {
            console.error("send message error:", err);
            setToast?.({ msg: err?.code === "permission-denied" ? "Permission denied." : "Failed to send message", type: "warning" });
        } finally {
            setSending(false);
        }
    }

    /* -------------------------
       Helpers & UI helpers
       ------------------------- */
    function formatDate(ts) {
        if (!ts) return "";
        if (typeof ts.toDate === "function") return ts.toDate().toLocaleString();
        try { return new Date(ts).toLocaleString(); } catch { return ""; }
    }

    function truncate(s, n = 60) {
        if (!s) return "";
        return s.length > n ? s.slice(0, n - 1) + "…" : s;
    }

    const tabCounts = useMemo(() => ({
        primary: primaryConvs.length,
        business: businessConvs.length,
        general: generalConvs.length,
        all: conversations.length
    }), [primaryConvs.length, businessConvs.length, generalConvs.length, conversations.length]);

    const headerProfile = useMemo(() => {
        if (selectedPartnerProfile) return selectedPartnerProfile;
        const conv = conversations.find((c) => {
            const partnerUid = c.partnerUid || (c.participants || []).find((p) => p !== currentUid);
            return partnerUid === selectedPartnerUid;
        });
        if (conv?.partnerProfile) return conv.partnerProfile;
        if (selectedPartnerUid) return { displayName: selectedPartnerUid, handle: selectedPartnerUid };
        return null;
    }, [selectedPartnerProfile, conversations, selectedPartnerUid, currentUid]);

    if (!open) return null;

    return (
        <Modal
            title="Messages"
            onClose={() => {
                setSelectedPartnerUid(null);
                setSelectedPartnerProfile(null);
                setComposerText("");
                onClose?.();
            }}
            size="full"
            className="messages-modal"
        >
            <div className="mim-container" ref={modalContentRef} onMouseDown={(e) => e.stopPropagation()}>
                {/* LEFT */}
                <aside className="mim-left" aria-label="Conversations">
                    <div className="mim-left-top">
                        <div className="mim-tabs" role="tablist" aria-label="Conversation categories">
                            <button className={`mim-tab ${activeTab === "primary" ? "active" : ""}`} onClick={() => setActiveTab("primary")}>Primary <span className="tab-count">{tabCounts.primary}</span></button>
                            <button className={`mim-tab ${activeTab === "business" ? "active" : ""}`} onClick={() => setActiveTab("business")}>Business<span className="tab-count">{tabCounts.business}</span></button>
                            <button className={`mim-tab ${activeTab === "general" ? "active" : ""}`} onClick={() => setActiveTab("general")}>General <span className="tab-count">{tabCounts.general}</span></button>
                            <button className={`mim-tab ${activeTab === "all" ? "active" : ""}`} onClick={() => setActiveTab("all")}>All <span className="tab-count">{tabCounts.all}</span></button>
                        </div>

                        <div className="mim-search">
                            <input ref={searchRef} type="search" placeholder="Search conversations or messages" value={search} onChange={(e) => setSearch(e.target.value)} className="mim-search-input" aria-label="Search conversations" />
                        </div>
                    </div>

                    <div className="mim-list" role="list">
                        {conversationsLoading && <div className="muted pad">Loading conversations…</div>}
                        {!conversationsLoading && conversations.length === 0 && <div className="muted pad">No conversations yet.</div>}

                        {conversations.map((c) => {
                            const partnerUid = c.partnerUid || (c.participants || []).find((p) => p !== currentUid) || "unknown";

                            // prefer conversation's partnerProfile, otherwise use profilesMap (preloaded), else empty object
                            const profile = c.partnerProfile && Object.keys(c.partnerProfile).length > 0 ? c.partnerProfile : (profilesMap[partnerUid] || {});
                            const displayName = profile.displayName || profile.name || profile.fullName || null;
                            const handle = profile.handle || profile.username || null;

                            const preview = c.lastMessage?.text || "";
                            const unread = c.unreadCounts?.[currentUid] || 0;
                            const ts = c.lastMessage?.createdAt || c.lastUpdated;

                            return (
                                <div
                                    key={c.id}
                                    onClick={() => { setSelectedPartnerUid(partnerUid); setSelectedPartnerProfile(c.partnerProfile || null); setComposerText(""); }}
                                    role="listitem"
                                    tabIndex={0}
                                    onKeyDown={(e) => { if (e.key === "Enter") { setSelectedPartnerUid(partnerUid); setSelectedPartnerProfile(c.partnerProfile || null); setComposerText(""); } }}
                                    className={`mim-conv-item ${selectedPartnerUid === partnerUid ? "selected" : ""}`}
                                    title={`${displayName || handle || partnerUid} — ${preview}`}
                                >
                                    <div className="conv-avatar" aria-hidden>
                                        {(displayName || handle || partnerUid).slice(0, 1).toUpperCase()}
                                    </div>

                                    <div className="conv-main">
                                        <div className="conv-row">
                                            <div className="conv-name-block">
                                                <div className="conv-name">{displayName || handle || partnerUid}</div>
                                            </div>
                                            <div className="conv-ts muted small">{formatDate(ts)}</div>
                                        </div>

                                        <div className="conv-sub muted">{truncate(preview, 80)}</div>
                                    </div>

                                    {unread > 0 && <div className="conv-unread" aria-label={`${unread} unread`}>{unread}</div>}
                                </div>
                            );
                        })}
                    </div>
                </aside>

                {/* RIGHT */}
                <section className="mim-right" aria-label="Conversation thread">
                    <header className="mim-thread-header">
                        {selectedPartnerUid ? (
                            <div className="mim-thread-meta">
                                <div className="mim-avatar" aria-hidden>{(headerProfile?.displayName || headerProfile?.handle || selectedPartnerUid).slice(0, 1).toUpperCase()}</div>
                                <div className="mim-thread-title">
                                    <div className="name">{headerProfile?.displayName || selectedPartnerUid}</div>
                                    <div className="muted small">@{headerProfile?.handle || selectedPartnerUid}</div>
                                </div>
                            </div>
                        ) : (
                            <div className="muted pad">Select a conversation</div>
                        )}
                    </header>

                    <div className="mim-thread-body" role="log" aria-live="polite">
                        {threadLoading && <div className="muted pad">Loading thread…</div>}
                        {!threadLoading && selectedPartnerUid && threadMessages.length === 0 && <div className="muted pad">No messages in this conversation.</div>}

                        {!threadLoading && threadMessages.map((m, i) => {
                            const mine = m.from === currentUid;
                            return (
                                <div key={m.id || `msg-${i}`} className={`mim-bubble ${mine ? "mine" : "theirs"}`}>
                                    <div className="mim-bubble-text">{m.text}</div>
                                    <div className="muted small mim-ts">{formatDate(m.createdAt)}</div>
                                </div>
                            );
                        })}
                    </div>

                    <form onSubmit={handleSendToPartner} className="mim-composer" aria-label="Message composer">
                        <input ref={composerRef} type="text" placeholder={selectedPartnerUid ? "Write a message…" : "Select a conversation to message"} value={composerText} onChange={(e) => setComposerText(e.target.value)} disabled={!selectedPartnerUid || sending} className="mim-input" onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendToPartner(e); } }} />

                        <div className="mim-actions">
                            <button type="submit" className="btn btn-primary" disabled={!selectedPartnerUid || sending}>{sending ? "Sending…" : "Send"}</button>
                        </div>
                    </form>
                </section>
            </div>
        </Modal>
    );
}

MessagesIndexModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    db: PropTypes.any,
    auth: PropTypes.any,
    user: PropTypes.object,
    useFirebase: PropTypes.bool,
    setToast: PropTypes.func,
    initialPartner: PropTypes.string
};

/* ---------------------------
   Helpers: markConversationReadSubcollection
   --------------------------- */
/* ---------------------------
   Helpers: markConversationReadSubcollection (improved)
   --------------------------- */
async function markConversationReadSubcollection(db, currentUid, partnerUid, setToast) {
    if (!db || !currentUid || !partnerUid) return [];

    try {
        const convId = [currentUid, partnerUid].sort().join("_");
        const itemsCol = collection(db, "messages", convId, "items");
        const q = query(
            itemsCol,
            where("to", "==", currentUid),
            where("from", "==", partnerUid),
            where("read", "==", false)
        );

        const snap = await getDocs(q);
        if (snap.empty) return [];

        const ids = [];
        const batch = writeBatch(db);

        snap.forEach((d) => {
            ids.push(d.id);
            batch.update(doc(db, "messages", convId, "items", d.id), { read: true, readAt: serverTimestamp() });
        });

        await batch.commit();

        // Best-effort: update the conversation doc so top-level listeners (Navbar) see unreadCounts=0
        try {
            const convRef = doc(db, "messages", convId);

            // Build an update payload. We prefer to zero unreadCounts[currentUid].
            // Also, if lastMessage matches partner->current, mark lastMessage.read = true
            const convSnap = await getDoc(convRef);
            const updatePayload = {};
            updatePayload[`unreadCounts.${currentUid}`] = 0;

            if (convSnap.exists()) {
                const convData = convSnap.data() || {};
                const last = convData.lastMessage || convData.last || null;
                if (last && last.from === partnerUid && last.to === currentUid) {
                    // update lastMessage.read and lastMessage.readAt
                    updatePayload['lastMessage.read'] = true;
                    updatePayload['lastMessage.readAt'] = serverTimestamp();
                }
            } else {
                // if doc doesn't exist, set participants/map (defensive)
                updatePayload.participants = [currentUid, partnerUid];
                updatePayload.participantMap = { [currentUid]: true, [partnerUid]: true };
            }

            // Try to merge the update; if permission-denied occurs, log & continue.
            await updateDoc(convRef, updatePayload);
        } catch (e) {
            // Not fatal — top-level update may fail due to security rules; log and continue
            console.warn("Could not update conversation doc (unreadCounts/lastMessage):", e);
        }

        return ids;
    } catch (err) {
        console.error("markConversationReadSubcollection failed:", err);
        setToast?.({ msg: "Failed to mark messages read.", type: "warning" });
        return [];
    }
}
